import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Save } from "lucide-react";

interface ScorecardHeaderProps {
	overallProgress: number;
	requiredProgress: number | null;
	hasChanges: boolean;
	saving: boolean;
	onSave: () => void;
	lastSavedAt: string | null;
	lastSavedByName: string | null;
}

/** Sticky action bar: progress summary, unsaved indicator, save button. */
export const ScorecardHeader: React.FC<ScorecardHeaderProps> = ({
	overallProgress,
	requiredProgress,
	hasChanges,
	saving,
	onSave,
	lastSavedAt,
	lastSavedByName,
}) => {
	const { t, i18n } = useTranslation();

	const savedDate = lastSavedAt
		? new Date(lastSavedAt).toLocaleString(i18n.language, {
				dateStyle: "medium",
				timeStyle: "short",
			})
		: null;

	return (
		<div className="sticky top-0 z-10 -mx-6 mb-4 border-y border-border bg-card/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/80">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
					<span>
						{t("candidates.tabsContent.scorecard.overallProgress", "Progress")}:{" "}
						<span className="font-medium text-foreground">{overallProgress}%</span>
					</span>
					{requiredProgress !== null && (
						<span>
							{t("candidates.tabsContent.scorecard.requiredProgress", "Required")}:{" "}
							<span className="font-medium text-foreground">{requiredProgress}%</span>
						</span>
					)}
					{savedDate && (
						<span>
							{lastSavedByName
								? t("candidates.tabsContent.scorecard.lastSavedBy", "Last saved by {{name}} · {{date}}", {
										name: lastSavedByName,
										date: savedDate,
									})
								: t("candidates.tabsContent.scorecard.lastSavedAt", "Last saved · {{date}}", {
										date: savedDate,
									})}
						</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					{hasChanges && (
						<Badge
							variant="outline"
							className="border-amber-500/50 text-amber-600 dark:text-amber-500"
						>
							{t("candidates.tabsContent.scorecard.unsavedChanges", "Unsaved changes")}
						</Badge>
					)}
					<Button size="sm" onClick={onSave} disabled={!hasChanges || saving}>
						<Save className="mr-2 h-3.5 w-3.5" />
						{saving
							? t("candidates.tabsContent.scorecard.saving", "Saving...")
							: t("candidates.tabsContent.scorecard.save", "Save scorecard")}
					</Button>
				</div>
			</div>
		</div>
	);
};
